import { Verdict } from '@exam-management/graphql';
import { ReviewResultLabel } from '@exam-management/task-management';
import { CheckIcon, CloseIcon, Flex, FormRadioSelection, Skeleton, Text } from '@shared/components';

import { useReviewResult, useReviewResultFormContext } from '../../providers';

interface RadioOptionProps {
  icon: JSX.Element;
  label: string;
}

function RadioOption(props: RadioOptionProps) {
  const { icon, label } = props;
  return (
    <Flex sx={{ gap: (theme) => theme.spacing(2) }}>
      {icon}
      <Text variant='body2'>{label}</Text>
    </Flex>
  );
}

const options = [
  {
    label: (
      <RadioOption
        icon={<CheckIcon sx={{ color: (theme) => theme.palette.grey[600] }} />}
        label='เห็นด้วย'
      />
    ),
    value: Verdict.Approved,
  },
  {
    label: (
      <RadioOption
        icon={<CloseIcon sx={{ color: (theme) => theme.palette.grey[600] }} />}
        label='ไม่เห็นด้วย'
      />
    ),
    value: Verdict.Rejected,
  },
];

interface ReviewResultFormProps {
  variant?: 'HIDDEN' | 'EMPTY' | 'PROHIBITED' | 'EDITABLE' | 'READONLY' | null;
}

export default function ReviewResultForm(props: Readonly<ReviewResultFormProps>) {
  const { variant = 'EMPTY' } = props;
  const formContext = useReviewResultFormContext();
  const {
    verdict: { loading: isLoading },
    reviewQSetQuestion,
  } = useReviewResult();

  const handleOnChange = (_: React.ChangeEvent<HTMLInputElement>, value: string) => {
    reviewQSetQuestion({
      verdict: value as Verdict,
    });
  };

  if (isLoading) {
    return (
      <Flex sx={{ gap: (theme) => theme.spacing(4) }}>
        <Skeleton width={200} height={52} />
        <Skeleton width={200} height={52} />
      </Flex>
    );
  }

  switch (variant) {
    case 'EMPTY':
    case 'PROHIBITED':
      return <ReviewResultLabel variant='CONTAINED' />;
    case 'EDITABLE':
      return (
        <FormRadioSelection
          name='verdict'
          options={options}
          control={formContext.control}
          onChange={handleOnChange}
          radioProps={{
            sx: (theme) => ({
              '& .MuiRadio-root': {
                p: 0,
                color: theme.palette.grey[400],
                '&.Mui-checked .MuiSvgIcon-root': {
                  color: (theme) => theme.palette.primary.main,
                },
              },
            }),
          }}
          sx={(theme) => ({
            '& .MuiFormControlLabel-root': {
              width: '200px',
              border: `1px solid ${theme.palette.grey[300]}`,
              borderRadius: theme.shape.rounded(1.5),
              p: theme.spacing(3, 4),
              ml: 0,
              flexDirection: 'row-reverse',
              justifyContent: 'space-between',
            },
            '& .MuiFormControlLabel-root:has(.MuiRadio-root.Mui-checked)': {
              borderColor: theme.palette.primary.main,
            },
          })}
          row
        />
      );
    case 'READONLY':
      return <ReviewResultLabel variant='CONTAINED' result={formContext.watch('verdict')} />;
    default:
      return null;
  }
}
